import React from 'react';
import styled, { css } from 'styled-components';
import { Check } from 'lucide-react';
import { semanticColors } from '@/common/styles/colorTokens';

interface StepItemType {
  id: number;
  label: string;
  status: 'current' | 'completed' | 'pending' | 'disabled';
}

interface StepProps {
  steps: StepItemType[];
  direction?: 'horizontal' | 'vertical';
  className?: string;
}

const getSizeStyles = (size: 'sm' | 'md' | 'lg' = 'md') => {
  switch (size) {
    case 'sm':
      return {
        width: '32px',
        height: '16px',
        thumbSize: '14px',
        thumbOffset: '1px',
        thumbCheckedOffset: '17px'
      };
    case 'lg':
      return {
        width: '52px',
        height: '26px',
        thumbSize: '22px',
        thumbOffset: '2px',
        thumbCheckedOffset: '28px'
      };
    case 'md':
    default:
      return {
        width: '44px',
        height: '22px',
        thumbSize: '18px',
        thumbOffset: '2px',
        thumbCheckedOffset: '24px'
      };
  }
};

const StepContainer = styled.div<{ $direction: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-direction: ${props => props.$direction === 'horizontal' ? 'row' : 'column'};
  align-items: ${props => props.$direction === 'horizontal' ? 'center' : 'flex-start'};
  gap: ${props => props.$direction === 'horizontal' ? '0' : '32px'};
  width: ${props => props.$direction === 'horizontal' ? '100%' : 'auto'};
  margin: 1rem;
`;

const StepItemContainer = styled.div<{ 
  $direction: 'horizontal' | 'vertical';
  $status: StepItemType['status'];
  $isLast: boolean;
}>`
  display: flex;
  flex-direction: ${props => props.$direction === 'horizontal' ? 'column' : 'row'};
  align-items: center;
  gap: ${props => props.$direction === 'horizontal' ? '8px' : '12px'};
  flex: ${props => props.$direction === 'horizontal' ? '1' : '0'};
  width: ${props => props.$direction === 'horizontal' ? '100%' : 'auto'};
  position: relative;

  ${props => !props.$isLast && props.$direction === 'horizontal' && css`
    &::after {
      content: '';
      position: absolute;
      left: calc(50% + 20px);
      top: 20px;
      transform: translateY(-50%);
      height: 1px;
      width: 100%;
      background-color: ${props.$status === 'completed' 
        ? semanticColors.background.brand
        : semanticColors.border.tertiary};
    }
  `}

  ${props => !props.$isLast && props.$direction === 'vertical' && css`
    &::after {
      content: '';
      position: absolute;
      top: 40px;
      left: 20px;
      width: 1px;
      height: 32px;
      background-color: ${props.$status === 'completed' 
        ? semanticColors.background.brand
        : semanticColors.border.tertiary};
    }
  `}
`;

const StepCircle = styled.div<{ $status: StepItemType['status'] }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  background-color: ${props => {
    switch (props.$status) {
      case 'current':
        return semanticColors.background.brand;
      case 'completed':
        return semanticColors.background.primary;
      case 'pending':
        return semanticColors.background.secondary;
      case 'disabled':
        return semanticColors.background.disabled;
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'current':
        return semanticColors.text.white;
      case 'completed':
        return semanticColors.background.brand;
      case 'pending':
        return semanticColors.text.secondary;
      case 'disabled':
        return semanticColors.text.disabled;
    }
  }};
  border: ${props => {
    switch (props.$status) {
      case 'completed':
        return `2px solid ${semanticColors.background.brand}`;
      case 'pending':
        return `1px solid ${semanticColors.border.tertiary}`;
      default:
        return 'none';
    }
  }};
`;

const StepLabel = styled.span<{ 
  $status: StepItemType['status'];
  $direction: 'horizontal' | 'vertical';
}>`
  font-size: 14px;
  font-weight: 500;
  text-align: ${props => props.$direction === 'horizontal' ? 'center' : 'left'};
  color: ${props => {
    switch (props.$status) {
      case 'current':
        return semanticColors.background.brand;
      case 'completed':
        return semanticColors.text.tertiary;
      case 'pending':
        return semanticColors.text.primary;
      case 'disabled':
        return semanticColors.text.disabled;
    }
  }};
`;

const LabelText = styled.span<{ $status: StepItemType['status'] }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => {
    switch (props.$status) {
      case 'current':
        return semanticColors.text.primary;
      case 'completed':
        return semanticColors.text.primary;
      case 'pending':
        return semanticColors.text.secondary;
      case 'disabled':
        return semanticColors.text.disabled;
    }
  }};
`;

const StatusText = styled.span<{ $status: StepItemType['status'] }>`
  font-size: 12px;
  font-weight: 400;
  color: ${props => {
    switch (props.$status) {
      case 'current':
        return semanticColors.background.brand;
      case 'completed':
        return semanticColors.text.onSuccess.primary;
      case 'pending':
        return semanticColors.text.secondary;
      case 'disabled':
        return semanticColors.text.disabled;
    }
  }};
`;

const getStatusText = (status: StepItemType['status']) => {
  switch (status) {
    case 'current':
      return '진행중';
    case 'completed':
      return '완료';
    case 'pending':
      return '대기중';
    case 'disabled':
      return '비활성';
  }
};

const StepIcon = styled(Check)`
  width: 20px;
  height: 20px;
  color: ${semanticColors.background.brand};
`;

export const Step: React.FC<StepProps> = ({ 
  steps, 
  direction = 'horizontal',
  className 
}) => {
  return (
    <StepContainer $direction={direction} className={className}>
      {steps.map((step, index) => (
        <StepItemContainer 
          key={step.id} 
          $direction={direction}
          $status={step.status}
          $isLast={index === steps.length - 1}
        >
          <StepCircle $status={step.status}>
            {step.status === 'completed' ? (
              <StepIcon />
            ) : (
              step.id
            )}
          </StepCircle>
          <StepLabel $status={step.status} $direction={direction}>
            {step.label}
          </StepLabel>
        </StepItemContainer>
      ))}
    </StepContainer>
  );
};

export type { StepProps, StepItemType }; 